<script lang="ts">
    import Esi from '@shared/components/esi/Esi.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import BenefitCinema from '@tilelist/components/benefit-cinema/BenefitCinema.svelte';
    import BrandTile from '@tilelist/components/brand-tile/BrandTile.svelte';
    import EekSheet from '@tilelist/components/eek-sheet/EekSheet.svelte';
    import ProductComparisonBanner from '@tilelist/components/product-comparison/ProductComparisonBanner.svelte';
    import ProductTileItem from '@tilelist/components/product-tile-item/ProductTileItem.svelte';
    import SdaHoarding from '@tilelist/components/sda-hoarding/SdaHoarding.svelte';
    import SponsoredHintSheet from '@tilelist/components/sponsored-hint-sheet/SponsoredHintSheet.svelte';
    import SimilarProductHeader from '@tilelist/components/tile-items/SimilarProductHeader.svelte';
    import Slider from '@tilelist/components/tile-items/Slider.svelte';
    import {
        type ProductItem,
        type TileItem,
        type TileListItem,
        isProductItem,
    } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageContext } from '@tilelist/domain/composition/TileListPage.ts';
    import {
        type FeatureIndexOffset,
        computeFeatureIndices,
    } from '@tilelist/domain/external-data-attributes/ExternalDataAttributes.ts';
    import { shouldShowInstallments } from '@tilelist/domain/feature-visibility/InstallmentVisibility';
    import RatingSheets from '../rating-sheets/RatingSheets.svelte';

    type Props = {
        tileListItems: TileListItem[];
        context: TileListPageContext;
        showTopInfos?: boolean;
        cacheRestored?: boolean;
        onRedirectToPdp?: (item: TileItem) => void;
        onProductItemUpdated?: (item: ProductItem) => void;
        featureIndexOffset: FeatureIndexOffset;
    };

    let {
        tileListItems: externalTileList,
        context,
        showTopInfos = false,
        cacheRestored = true,
        onRedirectToPdp = () => {},
        onProductItemUpdated = () => {},
        featureIndexOffset,
    }: Props = $props();

    const wiring = injectWiring(['controller']);
    const controller = $derived(wiring.controller);

    const onex = wiring.onex;

    const tileListItems = $derived.by(() => {
        const _ = $state(externalTileList);
        return _;
    });

    /**
 *
 *
 *
 *
 *
 */
    const listItemKey = (item: TileListItem, i: number) => {
        if (!isProductItem(item)) return `${item.type}.${i}`;
        return `${item.type}.${item.localListPosition}.${item.product.id}`;
    };

    const columns = $derived(context?.layout?.columns);

    const showInstallments = $derived(shouldShowInstallments(context.searchContext));
    const showRadioEquipmentRegulation = $derived(
        onex.isToggleActive('ENABLE_FEATURE_FUAG_COMPONENT_ON_TILE') ?? false,
    );

    const featureIndices = $derived(computeFeatureIndices(tileListItems, featureIndexOffset));
</script>

<!--
Loads the benefit-cinema JS assets once per page. Must render regardless of whether
any cinema slots are present, so the oc-cinema-mount listeners are set up before
cinema content appears dynamically (e.g. after a filter change).
-->
<Esi src="/benefit-cinema/cinema/productlist/assets" />

<RatingSheets />
<EekSheet />
<SponsoredHintSheet />

<section class={['reptile-tile-list', { [`reptile-tile-list__column--${columns}`]: columns }]}>
    {#each tileListItems as item, i (listItemKey(item, i))}
        {#if isProductItem(item)}
            <ProductTileItem
                {cacheRestored}
                productItem={item}
                rule={context.rule}
                layout={context.layout}
                loadVariation={async (variationId) => {
                    const updated = await controller.loadVariation(item, variationId);
                    if (!updated) return;
                    tileListItems[i] = updated;
                    onProductItemUpdated(updated);
                }}
                loadColorVariation={async (payload) => {
                    const updated = await controller.loadColorVariation(item, payload);
                    if (!updated) return;
                    tileListItems[i] = updated;
                    onProductItemUpdated(updated);
                }}
                onSeen={async () => {
                    controller.trackVisibility(item);
                }}
                onTileClick={(event) => {
                    controller.trackClick(event, item);
                    onRedirectToPdp(item);
                }}
                {showRadioEquipmentRegulation}
                {showTopInfos}
                {showInstallments}
            />
        {:else if item.type === 'SPH'}
            <SimilarProductHeader />
        {:else if item.type === 'BrandTile'}
            <BrandTile
                {cacheRestored}
                campaign={item.campaign}
                localListPosition={item.localListPosition}
                actualListPosition={item.actualListPosition}
                featureIndex={featureIndices.get(i)?.featureIndex}
                rule={context.rule}
                onSeen={async () => {
                    controller.trackVisibility(item);
                }}
                onTileClick={() => {
                    controller.trackBrandTileClick(item.actualListPosition, context.rule);
                    onRedirectToPdp(item);
                }}
            />
        {:else if item.type === 'SLIDER'}
            <Slider {...item} />
        {:else if item.type === 'PCB'}
            <ProductComparisonBanner />
        {:else if item.type === 'SponsoredDisplayAd'}
            <SdaHoarding
                listSlot="list-slot"
                searchContext={item.searchContext}
                featureIndex={featureIndices.get(i)?.featureIndex}
                hidden={item.hidden}
            />
        {:else if item.type === 'BenefitCinema'}
            <BenefitCinema
                dreson={context.rule}
                position="list"
                tilelistSlotIndex={featureIndices.get(i)?.tilelistSlotIndex}
                featureIndex={featureIndices.get(i)?.featureIndex}
            />
        {/if}
    {/each}
</section>

<!-- svelte-ignore css_unused_selector -->
<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins';

    .reptile-tile-list {
        position: relative;
        display: grid;
        grid-auto-rows: minmax(0, min-content);
        row-gap: tokens.$oc-semantic-spacing-25;
        gap: tokens.$oc-semantic-spacing-25;
        margin: tokens.$oc-semantic-spacing-25;

        @include mixins.breakpoint-from-l($to: false, $containerFirst: false) {
            gap: tokens.$oc-semantic-spacing-50;
            margin: tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-50
                tokens.$oc-semantic-spacing-25;
        }

        &__column {
            &--one-two {
                grid-template-columns: minmax(0, 1fr);

                @include mixins.breakpoint-from-l($to: false, $containerFirst: false) {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
            }

            &--two-three {
                /*                                                               */
                grid-template-columns: repeat(2, minmax(0, 1fr));

                @include mixins.breakpoint-from-l($to: false, $containerFirst: false) {
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                }
            }
        }
    }
</style>
